import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Link, useParams } from "react-router-dom";

import {
  LayerAlias,
  LAYERS,
  UNITS_TRAINING_CLASSIFICATIONS,
  UnitTrainingClassificationTypes,
  UnitTypes,
  UserPolices
} from "constant";
import { useMapContext } from "context/MapContextProvider";
import features from "features";
import { getParsedCoordinates } from "helpers/getParsedCoordinates";
import useDeleteMarker from "hooks/useDeleteMarker";
import usePolicy from "hooks/usePolicy";
import useShiftMap from "hooks/useShiftMap";
import { RootStateInterface } from "reducer";
import { MapStateInterface } from "scenes/MapPage/ducks";
import { IModalState } from "scenes/Modal/ducks";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";

import PreloaderWrapper from "components/PreloaderWrapper/PreloaderWrapper";
import Slider from "components/Slider/Slider";
import IconButton from "components/buttons/IconButton/IconButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "components/buttons/Secondary/Secondary";

import ShortArrowIcon from "assets/icons/arrow-short-down.svg";
import PrevIcon from "assets/icons/caret-left.svg";
import NextIcon from "assets/icons/caret-right.svg";
import CloseIcon from "assets/icons/close.svg";
import EditIcon from "assets/icons/pencil.svg";
import TrashIcon from "assets/icons/trash.svg";

import styles from "./map-marker-details.module.scss";

const DEFAULT_WIKI_DESCRIPTION_HEIGHT = 140;

const MapMarkerDetails = () => {
  const dispatch = useDispatch();
  const { coordinateSystem } = useMapContext();

  const { id: projectId } = useParams();

  const {
    markerDetails,
    isMarkerDetailsLoading,
    isMarkerDeleteLoading,
    markersTrainingClassifyLoading
  } = useSelector<RootStateInterface, MapStateInterface>((state) => state.map);

  const { unitsCollection } = useSelector<
    RootStateInterface,
    UnitsManagerStateInterface
  >((state) => state.unitsManager);
  const { isModalOpened } = useSelector<RootStateInterface, IModalState>(
    (state) => state.modal
  );

  const { shiftMapPointDetails } = useShiftMap();

  const { hasPolicy } = usePolicy();
  // const [activeLayerTab, setActiveLayerTab] = useState(0);
  // const [previousLayerTab, setPreviousLayerTab] = useState(null);
  const [isWikiDescriptionExpanded, setIsWikiDescriptionExpanded] =
    useState<boolean>(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState<boolean>(false);

  const [maxHeight, setMaxHeight] = useState<number>( // TODO: create separate wrapping component for 'show more/less'
    DEFAULT_WIKI_DESCRIPTION_HEIGHT
  );
  const descriptionContentRef = useRef(null);

  const deleteMarker = useDeleteMarker();

  const onCloseMapMarkersDetails = useCallback(() => {
    dispatch(features.map.actions.clearPointDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!descriptionContentRef.current) return;

    const fullHeight = descriptionContentRef.current.scrollHeight;
    setIsDescriptionOverflowing(fullHeight > DEFAULT_WIKI_DESCRIPTION_HEIGHT);
    setMaxHeight(
      isWikiDescriptionExpanded ? fullHeight : DEFAULT_WIKI_DESCRIPTION_HEIGHT
    );
  }, [isWikiDescriptionExpanded, markerDetails?.wikiData]);

  useEffect(() => () => onCloseMapMarkersDetails(), [onCloseMapMarkersDetails]);

  // const handleLayerButtonClick = (index) => {
  //   setPreviousLayerTab(activeLayerTab);
  //   setActiveLayerTab(index);
  // };

  const originalImages = useMemo(
    () =>
      markerDetails?.images?.filter((imgLink) => imgLink.includes("_original")),
    [markerDetails]
  );

  const previewImages = useMemo(() => {
    const nonOriginalImages = markerDetails?.images?.filter(
      (imgLink) => !imgLink.includes("_original")
    );

    return nonOriginalImages?.length > 0
      ? nonOriginalImages
      : markerDetails?.images;
  }, [markerDetails]);

  const onToggleWikiDescription = useCallback(() => {
    setIsWikiDescriptionExpanded((prevState) => !prevState);
  }, []);

  const totalSameLayerMarkers = useMemo(
    // TODO remove total unitsCollection[markerDetails?.alias]?.Point?.total
    () => unitsCollection[markerDetails?.alias]?.Point?.units.length,
    [markerDetails?.alias, unitsCollection]
  );

  const markerCurrentIndex = useMemo(() => {
    return unitsCollection[markerDetails?.alias]?.Point?.units?.findIndex(
      (unit) => unit.id === markerDetails?.id
    );
  }, [markerDetails?.alias, markerDetails?.id, unitsCollection]);

  const onUnitTrainingClassificationChange = useCallback(
    (type) => {
      dispatch(
        features.map.actions.updatePointTrainingClassificationRequest({
          params: {
            projectId,
            unitId: markerDetails.id,
            alias: markerDetails.alias,
            type: UnitTypes.POINT
          },
          fields: { classification: type },
          onSuccess: (res) => {
            const updatedClassification = UNITS_TRAINING_CLASSIFICATIONS.find(
              (item) =>
                item.type === res.data.additional.training_classification
            );
            toastr.success(
              `Done`,
              `Marker classification updated as ${updatedClassification.name}`
            );
          }
        })
      );
    },
    [dispatch, markerDetails?.id, projectId, markerDetails?.alias]
  );

  const getTrainingClassificationButtonLabel = useCallback(
    (trainingClassification) => {
      return markerDetails?.additional?.training_classification ===
        trainingClassification.type
        ? `${trainingClassification.name} copied`
        : `Copy ${trainingClassification.name}`;
    },
    [markerDetails]
  );

  const trainClassificationLoading = useCallback(() => {
    return markersTrainingClassifyLoading.find(
      (item) => item?.id === markerDetails?.id
    )?.training_classification;
  }, [markersTrainingClassifyLoading, markerDetails?.id]);

  const parseFields = useCallback(
    (data) => {
      const { name, object_diameter, coordinates, classification } = data;
      const geo_data = {
        coordinates: Object.values(coordinates),
        type: "Point"
      };

      const additional = {
        ...markerDetails?.additional,
        ...{
          object_diameter: object_diameter
            ? parseFloat(object_diameter)
            : undefined,
          classification
        }
      };

      return {
        name,
        additional,
        geo_data
      };
    },
    [markerDetails?.additional]
  );

  const editClassification = useCallback(
    (updatedValues) => {
      const updatedFields = parseFields(updatedValues);

      dispatch(
        features.unitsManager.actions.updateUnitRequest({
          params: {
            projectId,
            unitId: markerDetails.id,
            type: UnitTypes.POINT,
            alias: markerDetails.alias
          },
          fields: {
            ...markerDetails,
            ...updatedFields
          },
          onSuccess: (res) => {
            dispatch(features.modal.actions.hideModal());
            dispatch(
              features.map.actions.setPointDetails({
                ...res.data,
                alias: markerDetails.alias
              })
            );

            toastr.success(`Done`, `Point updated`);
          }
        })
      );
    },
    [dispatch, markerDetails, projectId, parseFields]
  );

  const onEditClassification = useCallback(() => {
    dispatch(
      features.unitsManager.actions.fetchUnitsClassificationsRequest({
        onSuccess: (res) => {
          dispatch(
            features.modal.actions.showModal({
              modalType: "CLASSIFICATION_POINT_MODAL",
              modalProps: {
                pointsLayerAlias: markerDetails.alias,
                currentClassificationData:
                  markerDetails?.additional?.classification,
                generalPointData: {
                  name: markerDetails?.name,
                  coordinates: {
                    lat: markerDetails?.geo_data?.coordinates[0],
                    lng: markerDetails?.geo_data?.coordinates[1]
                  },
                  object_diameter: markerDetails?.additional?.object_diameter
                },
                classificationsData: res.data[0],
                params: res.params,
                onSubmit: editClassification
              }
            })
          );
        }
      })
    );
  }, [editClassification, dispatch, markerDetails]);

  const onPaginateMarker = useCallback(
    (isNext) => () => {
      let index;
      const isRangeLimitMax = totalSameLayerMarkers === markerCurrentIndex + 1;
      const isRangeLimitMin = markerCurrentIndex + 1 === 1;

      if (isRangeLimitMax && isNext) index = 0;
      else if (isRangeLimitMin && !isNext) index = totalSameLayerMarkers - 1;
      else index = isNext ? markerCurrentIndex + 1 : markerCurrentIndex - 1;

      const id = unitsCollection[markerDetails?.alias].Point.units[index]?.id;

      if (id) {
        dispatch(
          features.unitsManager.actions.fetchUnitRequest({
            params: {
              projectId,
              alias: markerDetails?.alias,
              id
            },
            onSuccess: (res) => {
              shiftMapPointDetails(res.data.geo_data.coordinates);
            }
          })
        );
      }
    },
    [
      dispatch,
      markerCurrentIndex,
      markerDetails?.alias,
      projectId,
      shiftMapPointDetails,
      totalSameLayerMarkers,
      unitsCollection
    ]
  );

  const onDeleteMarker = useCallback(() => {
    const isNext = totalSameLayerMarkers !== markerCurrentIndex + 1;

    deleteMarker(
      markerDetails?.id,
      markerDetails?.alias,
      totalSameLayerMarkers > 1 ? onPaginateMarker(isNext) : () => {}
    )();
  }, [
    deleteMarker,
    markerCurrentIndex,
    markerDetails?.alias,
    markerDetails?.id,
    onPaginateMarker,
    totalSameLayerMarkers
  ]);

  useEffect(() => {
    if (totalSameLayerMarkers === 0) {
      onCloseMapMarkersDetails();
    }
  }, [onCloseMapMarkersDetails, totalSameLayerMarkers]);

  useEffect(() => {
    if (markerDetails?.isActive && !isModalOpened) {
      const handleKeyDown = (event) => {
        if (event.key === "Backspace") {
          onDeleteMarker();
        }
        if (event.key === "ArrowLeft") {
          onPaginateMarker(false)();
        }
        if (event.key === "ArrowRight") {
          onPaginateMarker(true)();
        }
        if (event.key === "Escape") {
          onCloseMapMarkersDetails();
        }
        if (event.ctrlKey && event.code === "KeyP") {
          event.preventDefault();
          onUnitTrainingClassificationChange(
            UnitTrainingClassificationTypes.TRUE_POSITIVE
          );
        }
        if (event.ctrlKey && event.code === "KeyN") {
          event.preventDefault();
          onUnitTrainingClassificationChange(
            UnitTrainingClassificationTypes.FALSE_POSITIVE
          );
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [
    onDeleteMarker,
    markerDetails?.isActive,
    onPaginateMarker,
    onCloseMapMarkersDetails,
    onUnitTrainingClassificationChange,
    isModalOpened
  ]);

  const parsedCoordinates = useMemo(() => {
    return getParsedCoordinates({
      coordinates: markerDetails?.geo_data?.coordinates,
      coordinateSystem
    });
  }, [markerDetails?.geo_data?.coordinates, coordinateSystem]);

  return (
    <div
      className={classNames(styles["details"], {
        [styles["details--active"]]: markerDetails?.isActive
      })}
      id="pointDetails"
    >
      <div className={styles["details-wrapper"]}>
        <div className={styles["details-content"]}>
          <PreloaderWrapper
            loading={isMarkerDetailsLoading || isMarkerDeleteLoading}
            isAbsolute
            title={"Information loading. Please wait."}
          >
            <div className={styles["details__content-flex-container"]}>
              <div className={styles["details__close"]}>
                <img
                  src={CloseIcon}
                  alt="close-details"
                  onClick={onCloseMapMarkersDetails}
                />
              </div>
              <div className={styles["details__head"]}>
                {/* {activeMarker && layers.length > 1 && (
                  <div className={styles["sensor-fusing__container"]}>
                    <span className={styles["sensor-fusing__name"]}>
                      {activeMarker.name}
                    </span>
                    <span className={styles["sensor-fusing__name"]}>
                      {Math.round(activeMarker.additional.probability * 100)}%
                    </span>
                  </div>
                )} */}
                <div className={styles["head__content"]}>
                  <div className={styles["head__top"]}>
                    <label>{markerDetails?.name}</label>
                    <div className={styles["pagination"]}>
                      <label>
                        Point {markerCurrentIndex + 1} of{" "}
                        {totalSameLayerMarkers}
                      </label>
                      <IconButton
                        onClick={onPaginateMarker(false)}
                        icon={PrevIcon}
                        disabled={totalSameLayerMarkers === 1}
                        alt="prev-marker-icon"
                      />
                      <IconButton
                        onClick={onPaginateMarker(true)}
                        disabled={totalSameLayerMarkers === 1}
                        icon={NextIcon}
                        alt="next-marker-icon"
                      />
                    </div>
                  </div>
                  {markerDetails &&
                    (markerDetails?.alias === LayerAlias.MANUAL ||
                      markerDetails?.additional?.probability) && (
                      <span
                        style={{
                          backgroundColor:
                            markerDetails?.additional?.classification?.color ??
                            LAYERS[markerDetails.alias].color
                        }}
                        className={styles["probability"]}
                      >
                        {markerDetails?.alias === LayerAlias.MANUAL
                          ? "manual"
                          : `${Math.round(
                              markerDetails?.additional?.probability * 100
                            )}%`}
                      </span>
                    )}
                  {/* {markerDetails?.wikiData?.tags?.map((item, index) => (
                    <div className={styles["head__tags"]} key={index}>
                      {item}
                    </div>
                  ))} */}
                  {parsedCoordinates && (
                    <div className={styles["head__coordinate"]}>
                      {parsedCoordinates}
                    </div>
                  )}
                </div>
              </div>
              {/* TODO */}
              {/* {layers.length > 0 && (
                <div className={styles["details__layers-container"]}>
                  {layers.map((el, index) => (
                    <DetectionTypeButton
                      name={el.name}
                      key={`${el.alias}:${index}`}
                      color={el.color}
                      probability={Math.round(
                        activeMarker?.additional.probability * 100
                      )}
                      index={index}
                      activeLayerTab={activeLayerTab}
                      previousLayerTab={previousLayerTab}
                      onClick={handleLayerButtonClick}
                    />
                  ))}
                </div>
              )} */}
              {markerDetails?.images?.length > 0 && (
                <div className={styles["details__slider"]}>
                  {previewImages.length > 0 && (
                    <Slider images={previewImages} />
                  )}
                  {originalImages.map((imageLink, index) => (
                    <div
                      className={styles["image-links-container"]}
                      key={`${imageLink}:${index}`}
                    >
                      <Link
                        to={imageLink}
                        target={"blank"}
                        rel="noopener noreferrer"
                        className={styles["image-link"]}
                      >
                        {imageLink}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles["details__buttons"]}>
                {hasPolicy(UserPolices.PROJECT_PROVIDER_UNIT_DELETE) && (
                  <SecondaryButton icon={TrashIcon} onClick={onDeleteMarker}>
                    Delete
                  </SecondaryButton>
                )}
                {UNITS_TRAINING_CLASSIFICATIONS.map((item, index) => (
                  <SecondaryButton
                    isLoading={trainClassificationLoading() === item.type}
                    onClick={() =>
                      onUnitTrainingClassificationChange(item.type)
                    }
                    isDisable={
                      markerDetails?.alias === LayerAlias.MANUAL ||
                      trainClassificationLoading() ||
                      markerDetails?.additional?.training_classification ===
                        item.type
                    }
                    key={`${item.name}-${index}`}
                  >
                    {getTrainingClassificationButtonLabel(item)}
                  </SecondaryButton>
                ))}
              </div>
              <div className={styles["details__classification"]}>
                <h3>
                  Classifications{" "}
                  {markerDetails?.additional?.classification?.name}
                </h3>
                <ul>
                  {markerDetails?.additional?.classification?.fields?.map(
                    (field, index) => (
                      <li key={`${field.name}-${index}`}>
                        <p>{field.name}</p>
                        <p>{field.value ?? "-"}</p>
                      </li>
                    )
                  )}
                </ul>
                {markerDetails?.additional?.classification ? (
                  <SecondaryButton
                    icon={EditIcon}
                    onClick={onEditClassification}
                  >
                    Edit
                  </SecondaryButton>
                ) : (
                  <PrimaryButton onClick={onEditClassification}>
                    Add classifications
                  </PrimaryButton>
                )}
              </div>
              {markerDetails?.wikiData && (
                <>
                  <div
                    ref={descriptionContentRef}
                    dangerouslySetInnerHTML={{
                      __html: markerDetails.wikiData.description
                    }}
                    className={classNames(styles["details__description"], {
                      [styles["details__description-expanded"]]:
                        isWikiDescriptionExpanded
                    })}
                    style={{
                      maxHeight: `${maxHeight}px`
                    }}
                  />
                  {isDescriptionOverflowing && (
                    <div
                      className={styles["expand-text-block"]}
                      onClick={onToggleWikiDescription}
                    >
                      <img
                        src={ShortArrowIcon}
                        alt="arrow"
                        className={classNames(styles["expand-text-icon"], {
                          [styles["expand-text-icon__inverted"]]:
                            isWikiDescriptionExpanded
                        })}
                      />
                      <span className={styles["expand-text"]}>
                        {isWikiDescriptionExpanded ? "Read less" : "Read more"}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </PreloaderWrapper>
        </div>
      </div>
    </div>
  );
};

export default MapMarkerDetails;
